import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';

import './styles.scss';
import { useDataFetch } from '@common/hooks/dataFetch';
import { useTranslation } from '@common/hooks/translation';
import { GetArrayMarket } from '@common/helpers/util/swarmTreeDestructuring';
import { fetchRequest } from '@common/helpers/util/requestMiddleware';
import { RacingMarketTypes } from '@common/constants/enums/racingMarketTypes';
import { eventsListSorter } from '@racings/helpers/eventsListSorter';
import { SportTypesUrls } from '@racings/constants/enums/sportTypes';
import { RacingEvent } from '../racingEvent';

export const RacingGame = ({
  game, activeGameId, competitionName, routePrefix,
}) => {
  const t = useTranslation();
  const { sportType } = useParams();
  const [activeMarketType, setActiveMarketType] = useState('Winner');
  const [racingData] = useDataFetch(
    () => fetchRequest('https://horseracing.betcoapps.com', `rc/${game.id}.json`, 'GET'),
    [],
  );

  const markets = GetArrayMarket(game.market);
  const marketsList = useMemo(() => {
    const types = Array.from(new Set(markets.map((m) => m.market_type)));
    return types.map((type) => ({ type, label: type }));
  }, [markets]);

  useEffect(() => {
    if (marketsList.length && !marketsList.some((m) => m.type === activeMarketType)) {
      // Prefer Winner if present, else first available type
      const winnerExists = marketsList.some((m) => m.type === 'Winner');
      setActiveMarketType(winnerExists ? 'Winner' : marketsList[0].type);
    }
  }, [marketsList, activeMarketType]);

  const winnerMarket = markets.find((market) => market.market_type === 'Winner');
  const currentMarket = markets.find(
    (market) => market.market_type === activeMarketType,
  ) || winnerMarket;
  const events = currentMarket?.event && Object.values(currentMarket.event).sort(eventsListSorter);
  const extraInfo = currentMarket?.extra_info;
  const activeMarketIdNumeric = (
    activeMarketType === 'Winner' ? RacingMarketTypes.WINNER
      : activeMarketType === 'Forecast' ? RacingMarketTypes.FORECAST
      : activeMarketType === 'Tricast' ? RacingMarketTypes.TRICAST
      : RacingMarketTypes.WINNER
  );

  return (
    <div key={game.id} className="racing-game-container">
      <div className="info-container">
        <div
          className={classnames('racing-game-info', {
            row: !activeGameId,
          })}
        >
          <div className="region-info info-item">
            <span className={`flag icon-${game.region_alias?.replaceAll(' ', '').toLowerCase()} flag`} />

            {!!racingData?.raceNumber && <span className="competition-name">{t(`R ${racingData?.raceNumber}`)}</span>}

            <span className="competition-name">{competitionName}</span>
          </div>
          {!activeGameId && (
            <div className="game-date info-item">{moment.unix(game.start_ts).format('HH:mm - ddd DD MMM')}</div>
          )}
          <div className="game-number-wrapper info-item">
            {t('Round Id')}
            {' '}
            <span>{game.game_number}</span>
          </div>
          {extraInfo && activeMarketType === 'Winner' && (
            <div className="game-number-wrapper info-item">
              {t('Each Way')}
              {' '}
              <span>
                1/
                {extraInfo.EachWayK}
                {' '}
                {t('Odds')}
                {' '}
                {extraInfo.EachWayPlace}
                {' '}
                {t('Places')}
              </span>
            </div>
          )}
        </div>
        <div className="markets-list">
          {marketsList.map((market) => (
            <div
              key={market.type}
              onClick={() => setActiveMarketType(market.type)}
              className={classnames('market-item', {
                active: activeMarketType === market.type,
              })}
            >
              {market.label}
            </div>
          ))}
        </div>
      </div>
      <div className="events-container">
        <div className="racing-header">
          <span>{t('horse')}</span>
          {sportType === SportTypesUrls.HORSE && <span>{t('trainer/Jockey')}</span>}
          <div className="header-item-wrapper">
            <span>{t('previous odds')}</span>
            <span>{t('odds')}</span>
          </div>
        </div>
        {events?.map((event) => (
          <RacingEvent
            key={event.id}
            event={event}
            game={game}
            market={currentMarket}
            number={event.original_order}
            racingData={racingData}
            activeMarketId={activeMarketIdNumeric}
            currentMarketType={activeMarketType}
            marketDisplayKey={currentMarket?.display_key}
          />
        ))}
      </div>
    </div>
  );
};
